import { TransactionIndicator } from '@george-labs.com/design-system';
import type { ICON } from '@george-labs.com/george-assets/icons';

import type { Transaction, TransactionLocation } from '@shared/transactions-types';
import { t } from '@luna/i18n';
import { CardType } from '@shared/cards-types';

import { hasVoucher } from '../predicates/hasVoucher';
import type { TransactionPredicate } from '../predicates/types';
import { isHighlighted } from '../predicates/isHighlighted';
import { hasAttachment } from '../predicates/hasAttachment';

export enum INDICATOR_TYPE {
    ATTACHMENT = 'attachment',
    RECEIPT = 'receipt',
    LOCATION = 'location',
    HIGHLIGHT = 'highlight',
    ADDITIONAL_CARD = 'additional card',
}

interface Indicator {
    id: INDICATOR_TYPE;
    validator: TransactionPredicate;
    ariaLabel: () => string;
    icon: ICON;
}

export function getIndicatorIcons(
    transaction: Transaction,
    supportedIndicators: INDICATOR_TYPE[],
): Indicator[] {
    const config = getIndicatorConfig();
    return config.filter((indicator) => {
        const isSupported = supportedIndicators.includes(indicator.id);
        const isIndicated = indicator.validator(transaction);
        return isSupported && isIndicated;
    });
}

export function hasCoordinates({ lon, lat }: Pick<TransactionLocation, 'lon' | 'lat'>): boolean {
    return Boolean(lon && lat);
}

function getIndicatorConfig(): Indicator[] {
    return [
        {
            id: INDICATOR_TYPE.ATTACHMENT,
            validator: hasAttachment,
            ariaLabel: () => t('transaction.accessibility.indicator.attachment'),
            icon: TransactionIndicator.ICON.ATTACHMENT,
        },
        {
            id: INDICATOR_TYPE.HIGHLIGHT,
            validator: isHighlighted,
            ariaLabel: () => t('transaction.accessibility.indicator.highlight'),
            icon: TransactionIndicator.ICON.STAR,
        },
        {
            id: INDICATOR_TYPE.RECEIPT,
            validator: hasVoucher,
            ariaLabel: () => t('transaction.accessibility.indicator.receipt'),
            icon: TransactionIndicator.ICON.FILE,
        },
        {
            id: INDICATOR_TYPE.LOCATION,
            validator: hasLocation,
            ariaLabel: () => t('transaction.accessibility.indicator.location'),
            icon: TransactionIndicator.ICON.LOCATION,
        },
        {
            id: INDICATOR_TYPE.ADDITIONAL_CARD,
            validator: isAdditionalCardType,
            ariaLabel: () => t('transaction.accessibility.indicator.additionalCard'),
            icon: TransactionIndicator.ICON.CARD_ADDITIONAL,
        },
    ];
}

function hasLocation(transaction: Transaction): boolean {
    const { posData } = transaction;
    return hasCoordinates(posData ?? {});
}

function isAdditionalCardType(transaction: Transaction): boolean {
    return transaction.cardType === CardType.ADDITIONAL;
}
